import { noLeadingTrailingSpaces } from 'utils/validation';
import {
  ComparatorOptionsWithNoValueRequirement,
  CONDITION_TYPE_TO_LEFT_COL_TEXT,
  GOAL_TYPES,
  OCCURRENCE_CONDITION_QUERY_KEY,
  PURCHASE_CONDITION_QUERY_KEY,
} from 'modules/predictiveGoals/components/queryBuilder/constants';
import { localizeDateTime, DateFormat } from 'utils/l10n';
import {
  DURATION_UNIT_DAY_CONVERSIONS,
  MALFORMED_EVENT_QUERY,
  MALFORMED_OCCURRENCES_CONDITION,
  MALFORMED_PROPERTY_QUERY,
  MALFORMED_PURCHASE_CONDITION,
  PROVIDE_GOAL_DEFINITION,
  constructNumericalValueForPropertyValidation,
  MAX_GOAL_LIMIT,
} from './constants';
import {
  ClausePropertyComparison,
  DurationOption,
  Durations,
  KeyedConditionClause,
  Nullable,
  PredictiveAudienceGoal,
  PredictiveAudiencesVocabulary,
  PredictiveGoalQuery,
  PredictiveGoalQueryClause,
  PropertyValue,
  QueryBuilderOrClause,
  VocabularyDuration,
} from './types';

/** Duration */
export const durationComparator = (firstDuration: string, secondDuration: string) => {
  // Separate out number and duration unit (e.g. week)
  const [firstNumber, firstDurationUnit] = firstDuration.split('_');
  const [secondNumber, secondDurationUnit] = secondDuration.split('_');
  // Convert each duration into number of days
  const firstDurationInDays =
    DURATION_UNIT_DAY_CONVERSIONS[<Durations>firstDurationUnit] * Number(firstNumber);
  const secondDurationInDays =
    DURATION_UNIT_DAY_CONVERSIONS[<Durations>secondDurationUnit] * Number(secondNumber);
  return firstDurationInDays - secondDurationInDays;
};

export const prettyDuration = (duration: string) => {
  const splitted = duration.split('_');
  const isPlural = Number(splitted[0]) > 1;
  return `${splitted.join(' ')}${isPlural ? 's' : ''}`;
};

export const filterDeprecatedDurations = (duration: string) => {
  const deprecatedDurations = ['inception', '1_year'];
  // NOTE: Temporary filter until value is removed from durations
  return !deprecatedDurations.includes(duration);
};

export const parseWindowOptionsFromDurations = (
  vocabulary: Nullable<PredictiveAudiencesVocabulary>,
): DurationOption[] => {
  if (!vocabulary?.durations || !vocabulary.durations.length) {
    return [];
  }
  const durationsData: VocabularyDuration = vocabulary.durations[0];
  return [...durationsData.durations]
    .filter(filterDeprecatedDurations)
    .sort(durationComparator)
    .map((duration) => ({
      id: duration,
      label: prettyDuration(duration),
      value: duration,
    }));
};

export const firstWindowDurationFromGoal = (
  predictiveGoal: Pick<PredictiveAudienceGoal, 'goal'> | Partial<PredictiveAudienceGoal>,
) => prettyDuration(predictiveGoal?.goal?.and_clauses[0]?.or_clauses[0].window ?? '');

export const removeIterablePrefixFromString = (value: string): string => {
  const regExp = /^([Ii]terable [Uu]ser|[Ii]terable [Ss]ystem|[Ii]terable [Cc]ustom)\s{1,}/g;
  return value.replace(regExp, '');
};

/** Property */
export const parsePropertyValuesFromPropertyOptions = (values: string[]): PropertyValue[] =>
  values?.map((value) => ({
    id: value,
    label: value,
    value,
  }));

/** Event */
const conditionEventKeys = [OCCURRENCE_CONDITION_QUERY_KEY, PURCHASE_CONDITION_QUERY_KEY];
/**
 * @param { Object } clauseToParse - The running goal clause which will the collection of items under each or_clauses.
 * @returns { {ClausePropertyComparison & {key: CONDITION_TYPE_TO_LEFT_COL_TEXT}} }
 */
export function getEventConditionClauses(
  clause: PredictiveGoalQueryClause,
): Array<KeyedConditionClause> {
  return Object.entries(clause).reduce(
    (conditionClauses, [conditionKey, conditionClause]) => {
      if (conditionEventKeys.includes(conditionKey)) {
        conditionClauses.push({
          ...(conditionClause as ClausePropertyComparison),
          key: conditionKey as keyof typeof CONDITION_TYPE_TO_LEFT_COL_TEXT,
        });
      }
      return conditionClauses;
    },
    [] as Array<KeyedConditionClause>,
  );
}

export const isValidPurchaseQuery = (comparator: string, value: string | undefined): boolean => {
  if (ComparatorOptionsWithNoValueRequirement.includes(comparator)) {
    return true;
  }
  return typeof comparator !== 'undefined' && typeof value !== 'undefined' && value !== '';
};

export const isNumeric = (value: string) => {
  const isNumericRegex = /^[0-9]+$/;
  return isNumericRegex.test(value);
};

/** @ts-ignore query builder component also has ts-ignore */
export const validateQuery = (query) => {
  const errors = new Set<string>();
  if (!query || !query.and_clauses || query.and_clauses.length === 0) {
    errors.add(PROVIDE_GOAL_DEFINITION);
  } else {
    /** @ts-ignore */
    query.and_clauses?.forEach(({ or_clauses: orQuery }) => {
      /** @ts-ignore */
      orQuery?.forEach((q) => {
        switch (q?.type) {
          case GOAL_TYPES.property:
            if (!(q.name && q.property_comparison && q.property_comparison.comparator)) {
              errors.add(MALFORMED_PROPERTY_QUERY);
            } else if (
              !ComparatorOptionsWithNoValueRequirement.includes(q.property_comparison.comparator) &&
              !q.property_comparison.value
            ) {
              errors.add(MALFORMED_PROPERTY_QUERY);
            }
            break;
          case GOAL_TYPES.customEvent:
          case GOAL_TYPES.systemEvent:
          case GOAL_TYPES.purchaseEvent:
            if (!q.name) {
              errors.add(MALFORMED_EVENT_QUERY);
            }
            if (q.occurrence_comparison) {
              if (!(q.occurrence_comparison.comparator && q.occurrence_comparison.value)) {
                errors.add(MALFORMED_OCCURRENCES_CONDITION);
              }

              if (!isNumeric(q.occurrence_comparison.value)) {
                errors.add(constructNumericalValueForPropertyValidation(q.name));
              }
            }
            if (q.purchase_amount_comparison) {
              const isValid = isValidPurchaseQuery(
                q.purchase_amount_comparison.comparator,
                q.purchase_amount_comparison.value,
              );
              if (!isValid) {
                errors.add(MALFORMED_PURCHASE_CONDITION);
              }

              if (!isNumeric(q.purchase_amount_comparison.value)) {
                errors.add(constructNumericalValueForPropertyValidation(q.name));
              }
            }
            break;
          default:
            errors.add(MALFORMED_EVENT_QUERY);
        }
      });
    });
  }
  return errors;
};

/** Date stamp is formatted like "2021-10-04T10:48:48.275-07:00[America/Los_Angeles]"
 * LastModifiedCell does not like this format so we are simply removing the timezone at the end
 */
export const removeTimeZoneFromDateStamp = (dateStamp: string) => dateStamp.split('[')[0];

export const parseLastRefreshForDisplay = (dateStamp: string): string => {
  const removedTimeZone = removeTimeZoneFromDateStamp(dateStamp);
  const newDate = new Date(removedTimeZone);
  if (newDate instanceof Date && !Number.isNaN(newDate.valueOf())) {
    return localizeDateTime(new Date(removedTimeZone), {
      relative: true,
      dateStyle: DateFormat.Numeric,
    });
  } else {
    return '';
  }
};

export const ensureString = (
  maybeString: Nullable<string> | undefined,
  defaultText?: string,
): string => {
  if (!maybeString) {
    return defaultText || '';
  }
  return maybeString;
};

/**
 * @description Transformer function used to translate query builder schema to goal creation and goal edit query. query builder generates a schema that is generic that needs to be translate to the proper BE schema
 * @param { Object } goalQuery - The goal query from query builder
 * @returns { PredictiveGoalQuery } - The transformed goal format required by the goal creation endpoint
 */
export function transformForCreation(goalQuery: PredictiveGoalQuery): PredictiveGoalQuery {
  if (
    !goalQuery.and_clauses ||
    !Array.isArray(goalQuery.and_clauses) ||
    !Array.isArray(goalQuery.and_clauses[0]?.or_clauses)
  ) {
    return {
      and_clauses: [
        {
          or_clauses: [{} as any],
        },
      ],
    };
  }

  const transformedQuery = goalQuery.and_clauses.map((andClause) => {
    const orTransformed = andClause.or_clauses.map((orClause: QueryBuilderOrClause) => {
      const { eventConditions, ...restOr } = orClause;
      if (!eventConditions) {
        return orClause;
      }

      const transformedConditions = eventConditions.reduce((conditions, current) => {
        return {
          ...conditions,
          [current.queryKey]: current.query,
        };
      }, {});
      return {
        ...restOr,
        ...transformedConditions,
      };
    });
    return {
      ...andClause,
      or_clauses: orTransformed,
    };
  });

  return { and_clauses: transformedQuery };
}

/** Validate goal and property name */
export function isValidGoalProperty(property: string) {
  const errors = noLeadingTrailingSpaces(property);
  return errors.length <= 0;
}

export function hasReachedMaxGoalLimit(goalData: PredictiveAudienceGoal[]) {
  return goalData.length >= MAX_GOAL_LIMIT;
}
